import { type PropertyCard } from '@resolute/schema';
import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react';


import { useLocalStorage } from '@/lib/use-local-storage';

export const MAX_COMPARE = 4;

interface CompareContextValue {
  items: PropertyCard[];
  count: number;
  isFull: boolean;
  has: (id: string) => boolean;
  /** Returns false if the item couldn't be added (list full). */
  toggle: (property: PropertyCard) => boolean;
  remove: (id: string) => void;
  clear: () => void;
}

const CompareContext = createContext<CompareContextValue | null>(null);

export function CompareProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useLocalStorage<PropertyCard[]>('rps-compare', []);

  const has = useCallback((id: string) => items.some((p) => p.id === id), [items]);

  const toggle = useCallback(
    (property: PropertyCard): boolean => {
      const exists = items.some((p) => p.id === property.id);
      if (exists) {
        setItems((prev) => prev.filter((p) => p.id !== property.id));
        return true;
      }
      if (items.length >= MAX_COMPARE) return false;
      setItems((prev) => [...prev, property]);
      return true;
    },
    [items, setItems],
  );

  const remove = useCallback(
    (id: string) => setItems((prev) => prev.filter((p) => p.id !== id)),
    [setItems],
  );

  const clear = useCallback(() => setItems([]), [setItems]);

  const value = useMemo<CompareContextValue>(
    () => ({ items, count: items.length, isFull: items.length >= MAX_COMPARE, has, toggle, remove, clear }),
    [items, has, toggle, remove, clear],
  );

  return <CompareContext.Provider value={value}>{children}</CompareContext.Provider>;
}

export function useCompare(): CompareContextValue {
  const ctx = useContext(CompareContext);
  if (!ctx) throw new Error('useCompare must be used within a CompareProvider');
  return ctx;
}
